// HollowScript tokenisation for Monaco, driven by the native lexer through
// the `tooling` build's `highlight()` - not a second, hand-written grammar.
//
// `highlight()` is a whole-text, stateless lexical classifier: it needs no
// check, no capability interface and works on text that does not parse. Monaco
// asks for one line at a time, and every HollowScript token is confined to a
// single line (spec.md section 1.4 and 1.5 both make a raw newline inside a
// string or filter literal a lex error), so lexing just the current line's
// text reproduces the classification a whole-document lex would give for
// everything except a multi-line expression embedded inside a string
// interpolation `${ ... }` - the one construct whose *content* can
// legitimately cross a line boundary. That rare case degrades to imperfect
// highlighting on the affected lines only; it never touches what the real
// checker or the VM do. Offsets are UTF-16 code units, so they index a JS
// line string directly.
//
// Returns plain data (no Monaco types anywhere in this module) so it stays
// unit-testable with zero editor dependency and so the ~98MB `monaco-editor`
// package is never pulled into this module's own import graph - only the
// thin Studio-layer adapter that actually builds a
// `monaco.languages.TokensProvider` needs to know Monaco's shapes at all
// (`src/studio/editor/scripting/monaco/monacoAdapter.ts`).

import type { MonacoTooling } from '../nativeTooling.js';

/** The eight lexical classes the native `highlight()` answers. Local to
 * Studio so the editor does not depend on any language package's types; the
 * assignment from the native class below keeps the two honest at compile
 * time. */
export type HighlightClass = 'keyword' | 'identifier' | 'number' | 'string' | 'filter' | 'operator' | 'punctuation' | 'comment';

export interface HollowScriptToken {
  /** 0-based character offset into the line where this token's styling
   * begins; continues until the next token's `startIndex` (or the end of
   * the line). */
  readonly startIndex: number;
  /** `null` for an unstyled gap (whitespace, or a character the language has
   * no token for) - `highlight()` only returns ranges for real tokens and
   * comments, never for insignificant gaps. */
  readonly kind: HighlightClass | null;
}

/** Tokenises one line of HollowScript source (no trailing newline) into a
 * gap-filled, monotonically-increasing-`startIndex` token list - the shape
 * every line-based tokeniser (Monaco's `TokensProvider`) ultimately wants,
 * decoupled from that API. Always starts at index 0 (even when the line
 * begins mid-gap) and never throws on any text (`highlight` is total).
 *
 * Synchronous on purpose: Monaco's `TokensProvider.tokenize` cannot await, so
 * the caller hands over a `tooling` instance that is already loaded
 * (`getMonacoTooling()`), and registers its tokens provider only once it is. */
export function tokenizeHollowScriptLine(line: string, tooling: Pick<MonacoTooling, 'highlight'>): readonly HollowScriptToken[] {
  const ranges = tooling.highlight(line);
  // A blank or pure-whitespace line (no real tokens at all) is exactly one
  // unstyled gap covering the whole line - handled separately so the
  // trailing-gap push after the loop can never also fire for the same,
  // already-zero cursor position.
  if (ranges.length === 0) return [{ startIndex: 0, kind: null }];

  const tokens: HollowScriptToken[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) tokens.push({ startIndex: cursor, kind: null });
    tokens.push({ startIndex: range.start, kind: range.class });
    cursor = range.end;
  }
  if (cursor < line.length) tokens.push({ startIndex: cursor, kind: null });
  return tokens;
}
